import { t } from '@lingui/core/macro';
import {
  NON_BLOCKING_STEPS,
  NON_RETRYABLE_STEPS,
  STEP_FAILURE_PREFIX,
  STEP_HINTS,
  STEP_RUNNING_DETAILS,
  STEP_TITLES,
} from 'modules/overlay-v2/domain/constants/readiness';
import {
  OverlayReadinessSignals,
  OverlayReadinessState,
  OverlayReadinessStep,
  OverlayReadinessStepId,
  ReadinessTask,
} from 'modules/overlay-v2/domain/interfaces/readiness';
import { i18n, MessageDescriptor } from 'shared/i18n';

/** Порядок шагов на экране. Он же порядок выполнения: следующий начинается, когда закончился предыдущий. */
export const READINESS_STEP_ORDER: OverlayReadinessStepId[] = [
  'link',
  'subscription',
  'device',
  'match',
  'matchData',
  'lineups',
  'chronology',
  'matchTime',
  'template',
  'boards',
  'assets',
  'remote',
];

/** Итог одного шага до того, как к нему применили каскад «предыдущий ещё не закончился». */
type Resolution = Pick<OverlayReadinessStep, 'status' | 'detail' | 'hint'> & {
  status: Exclude<OverlayReadinessStep['status'], 'pending'>;
};

/** Подписи шагов — дескрипторы; переводятся здесь, в момент сборки, на текущем языке. */
const translate = (descriptor?: MessageDescriptor): string | undefined => (descriptor ? i18n._(descriptor) : undefined);

const done = (detail?: string): Resolution => ({ status: 'done', detail });
const running = (id: OverlayReadinessStepId, detail?: string, hint?: string): Resolution => ({
  status: 'running',
  detail: detail ?? translate(STEP_RUNNING_DETAILS[id]),
  hint,
});
const warning = (detail: string, hint: string): Resolution => ({ status: 'warning', detail, hint });
const failed = (detail: string, hint?: string): Resolution => ({ status: 'failed', detail, hint });

/** Шаг, целиком описываемый слайсом `readiness`: журнал матча, время, картинки. */
function resolveTask(id: OverlayReadinessStepId, task: ReadinessTask, doneDetail: string): Resolution {
  switch (task.status) {
    case 'done':
      return done(doneDetail);
    case 'failed':
      return failed(withPrefix(id, task.error), i18n._(STEP_HINTS.networkRetry));
    default:
      return running(id);
  }
}

function withPrefix(id: OverlayReadinessStepId, error?: string): string {
  const prefix = translate(STEP_FAILURE_PREFIX[id]) ?? t`Не получилось`;
  return error ? `${prefix}: ${error}` : prefix;
}

function resolveStep(id: OverlayReadinessStepId, signals: OverlayReadinessSignals): Resolution {
  switch (id) {
    case 'link':
      return signals.routeError
        ? failed(signals.routeError.title, signals.routeError.subtitle)
        : done(t`Организатор, устройство и доступ на месте`);

    case 'subscription': {
      const { isLoading, error, hasObsIntegration } = signals.subscription;
      if (error) return failed(withPrefix(id, error), i18n._(STEP_HINTS.networkRetry));
      if (isLoading) return running(id);
      if (!hasObsIntegration) return failed(t`Трансляция не подключена к этому организатору`, i18n._(STEP_HINTS.subscriptionMissing));
      return done(t`Трансляция оплачена`);
    }

    case 'device':
      // Сорванный запрос и «на устройстве просто нет матча» — разные вещи. Раньше первое
      // выглядело как второе, и оператору советовали запустить уже идущий матч.
      if (signals.device.status === 'failed') return failed(withPrefix(id, signals.device.error), i18n._(STEP_HINTS.networkRetry));
      return signals.isBroadcastInitialized ? done(t`Устройство на связи`) : running(id);

    case 'match':
      // Матч ещё не начали — это не поломка, а ожидание: крутимся дальше и объясняем,
      // что сделать. Как только матч запустят в приложении, шаг закроется сам.
      if (signals.isMatchAvailable === undefined) return running(id);
      if (!signals.isMatchAvailable) return running(id, t`Матч в приложении ещё не запущен`, i18n._(STEP_HINTS.matchWaiting));
      return done(t`Матч найден`);

    case 'matchData': {
      const { isLoaded, error } = signals.matchData;
      if (error) return failed(withPrefix(id, error), i18n._(STEP_HINTS.networkRetry));
      return isLoaded ? done(t`Счёт, фолы и составы загружены`) : running(id);
    }

    case 'lineups': {
      const players = signals.involvedPlayers;
      if (!players) return running(id);
      const { first, second } = players;
      const total = first + second;
      const detail = t`Вышли на матч: ${first} и ${second}`;
      return total === 0 ? warning(t`Ни одного заявленного игрока`, i18n._(STEP_HINTS.noLineups)) : done(detail);
    }

    case 'chronology':
      return resolveTask(id, signals.chronology, t`Журнал открыт`);

    case 'matchTime':
      return resolveTask(id, signals.matchTime, t`Секундомер и тайм сверены`);

    case 'template': {
      if (!signals.championship) return running(id);
      if (!signals.championship.hasTemplate) {
        const name = signals.championship.name ?? t`без названия`;
        return failed(t`У турнира «${name}» не выбрано оформление табло`, i18n._(STEP_HINTS.templateMissing));
      }
      // Сбой загрузки самого оформления — отдельный случай: без него шаг крутился бы вечно.
      if (signals.template.status === 'failed') {
        return failed(withPrefix(id, signals.template.error), i18n._(STEP_HINTS.networkRetry));
      }
      return signals.isTemplateLoaded ? done(signals.templateName ?? t`Оформление загружено`) : running(id);
    }

    case 'boards':
      const { boardsCount } = signals;
      if (boardsCount === 0) {
        const name = signals.templateName ?? t`без названия`;
        return failed(t`В оформлении «${name}» нет ни одного табло`, i18n._(STEP_HINTS.noBoards));
      }
      return done(t`Табло в оформлении: ${boardsCount}`);

    case 'assets': {
      const { status, error, report } = signals.assets;
      if (status === 'failed') return failed(withPrefix(id, error), i18n._(STEP_HINTS.networkRetry));
      if (status !== 'done') return running(id);
      if (!report || report.total === 0) return done(t`Картинок для загрузки нет`);
      const { loaded, total } = report;
      if (report.failed > 0 || report.isTimedOut) {
        return warning(t`Скачали ${loaded} из ${total}`, i18n._(STEP_HINTS.someImagesFailed));
      }
      return done(t`Скачали ${loaded} из ${total}`);
    }

    case 'remote':
      // Единственный необязательный шаг: пульт может не отозваться, и это переживаемо —
      // оверлей умеет сам опрашивать журнал матча раз в 3 секунды.
      if (signals.remote === 'connected') return done(t`Пульт на связи`);
      if (signals.remote === 'timedOut') return warning(t`Пульт не отозвался`, i18n._(STEP_HINTS.remoteTimedOut));
      return running(id);
  }
}

const isFinished = (status: OverlayReadinessStep['status']) => status === 'done' || status === 'warning';

/**
 * Собирает чеклист из сигналов. Чистая функция: ни React, ни Redux, ни Supabase —
 * вся лестница приоритетов проверяется обычными юнит-тестами.
 *
 * Обязательные шаги идут строго по очереди: пока текущий не закончился, следующие
 * показываются серыми. Необязательные (сейчас только связь с пультом) считаются
 * отдельно и не задерживают показ табло.
 */
export function buildReadinessSteps(signals: OverlayReadinessSignals): OverlayReadinessState {
  const steps: OverlayReadinessStep[] = [];
  let isBlocked = false;

  for (const id of READINESS_STEP_ORDER) {
    const isBlocking = !NON_BLOCKING_STEPS.includes(id);

    // Необязательный шаг живёт своей жизнью, но до появления матча показывать по нему нечего.
    const isGated = isBlocking ? isBlocked : signals.isMatchAvailable !== true;
    const resolution: Resolution | { status: 'pending' } = isGated ? { status: 'pending' } : resolveStep(id, signals);

    const step: OverlayReadinessStep = {
      id,
      title: i18n._(STEP_TITLES[id]),
      isBlocking,
      isRetryable: !NON_RETRYABLE_STEPS.includes(id),
      ...resolution,
    };

    if (step.status === 'failed' && step.isRetryable && signals.retry) {
      step.retryAt = signals.retry.retryAt;
    }

    steps.push(step);

    if (isBlocking && !isFinished(step.status)) isBlocked = true;
  }

  const blockingSteps = steps.filter((step) => step.isBlocking);

  return {
    steps,
    subtitle: buildSubtitle(signals),
    doneCount: steps.filter((step) => isFinished(step.status)).length,
    totalCount: steps.length,
    isReady: blockingSteps.every((step) => isFinished(step.status)),
    failedStepId: steps.find((step) => step.status === 'failed')?.id,
  };
}

function buildSubtitle(signals: OverlayReadinessSignals): string | undefined {
  const first = signals.teamNames?.first;
  const second = signals.teamNames?.second;
  if (!first && !second) return undefined;
  const firstName = first ?? t`Команда 1`;
  const secondName = second ?? t`Команда 2`;
  return `${firstName} — ${secondName}`;
}
